import React, { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRightIcon, ChevronDownIcon, CornerDownRightIcon, WorkflowIcon } from 'lucide-react';
import { SOLUTIONS_MENU, BUILDER_MENU_ITEM } from '../../data/solutionsMenu';
import { cn } from '../../src/lib/utils';
const itemClass = 'flex flex-col rounded-lg px-3 py-2.5 transition-colors hover:bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lime';

/** Desktop Solutions dropdown. Opens on click, hover or keyboard; closes on Escape / outside click. */
export function SolutionsMenu({
  active
}: {
  active: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLLIElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  const panelId = useId();
  const {
    pathname,
    hash
  } = useLocation();
  useEffect(() => setOpen(false), [pathname, hash]);
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const openNow = () => {
    window.clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 140);
  };
  return <li ref={rootRef} className="relative" onMouseEnter={openNow} onMouseLeave={closeSoon} onBlur={event => {
    if (!rootRef.current?.contains(event.relatedTarget as Node)) setOpen(false);
  }} data-id="element-470" data-mp="27ehy0am5hg7h">
      <button ref={buttonRef} type="button" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen(value => !value)} onKeyDown={event => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setOpen(true);
        window.requestAnimationFrame(() => rootRef.current?.querySelector<HTMLAnchorElement>('[data-menu-link]')?.focus());
      }
    }} className={cn('relative inline-flex items-center gap-1 whitespace-nowrap text-[13px] font-medium transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-4 focus-visible:ring-offset-ink 2xl:text-sm', active || open ? 'text-text-primary' : 'text-text-secondary')} data-id="element-471" data-mp="5i8znxndjayh">
        Solutions
        <ChevronDownIcon className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} aria-hidden="true" data-id="element-472" data-mp="2zahf0o4e4xa9" />
        {active ? <span aria-hidden="true" className="absolute -bottom-2 left-0 right-0 h-0.5 rounded-full bg-lime" data-id="element-473" data-mp="2mqkg6dnb0pt3" /> : null}
      </button>

      <div id={panelId} hidden={!open} className="absolute left-1/2 top-full z-50 w-[560px] -translate-x-1/2 pt-5" data-id="element-474" data-mp="31o31t74oqmwb">
        <div className="rounded-2xl border border-hairline bg-panel p-3 shadow-[0_30px_80px_-30px_rgba(20,14,60,0.35)]" data-id="element-475" data-mp="2wmnflbqruacc">
          <Link to="/solutions" data-menu-link className="flex items-center justify-between rounded-lg bg-surface px-3 py-2.5 text-sm font-semibold text-text-primary transition-colors hover:bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lime" data-id="element-476" data-mp="2402rql9i8gdj">
            All solutions
            <ArrowRightIcon className="h-4 w-4 text-lime" aria-hidden="true" data-id="element-477" data-mp="1q6jryvwlnlos" />
          </Link>
          <ul className="mt-2 grid grid-cols-2 gap-1" data-id="element-478" data-mp="1l9ba6wfd5jo2">
            {SOLUTIONS_MENU.map(item => <li key={item.href} data-id="element-479" data-mp="og3eg07wdwv4">
                <Link to={item.href} data-menu-link className={itemClass} data-id="element-480" data-mp="sji5rn1adznv">
                  <span className="text-sm font-semibold text-text-primary" data-id="element-481" data-mp="shf60bac0s5y">{item.label}</span>
                  <span className="text-xs text-text-tertiary" data-id="element-482" data-mp="xh1ksp5t6ggv">{item.descriptor}</span>
                </Link>
                {item.child ? <Link to={item.child.href} data-menu-link className={cn(itemClass, 'ml-3 flex-row items-start gap-2 py-2')} data-id="element-483" data-mp="179h17i1giwzn">
                    <CornerDownRightIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-lime" aria-hidden="true" data-id="element-484" data-mp="1c67gi35ftok0" />
                    <span className="flex flex-col" data-id="element-485" data-mp="1ga5f8cquoxcj">
                      <span className="text-[13px] font-semibold text-text-primary" data-id="element-486" data-mp="1l8p5gpch8vap">{item.child.label}</span>
                      <span className="text-xs text-text-tertiary" data-id="element-487" data-mp="3i1scux58zqhz">{item.child.descriptor}</span>
                    </span>
                  </Link> : null}
              </li>)}
          </ul>
          <Link to={BUILDER_MENU_ITEM.href} data-menu-link className="mt-2 flex items-center gap-3 rounded-lg border border-lime/20 bg-violet-soft px-3 py-2.5 transition-colors hover:border-lime/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lime" data-id="element-488" data-mp="3fh2ngyrrytin">
            <WorkflowIcon className="h-4 w-4 shrink-0 text-lime" aria-hidden="true" data-id="element-489" data-mp="29r5zvvf7hgm" />
            <span className="flex min-w-0 flex-1 flex-col" data-id="element-490" data-mp="27ethy8flzay">
              <span className="flex items-center gap-2 text-sm font-semibold text-text-primary" data-id="element-491" data-mp="3thdgo8gk4k6z">
                {BUILDER_MENU_ITEM.label}
                <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-lime" data-id="element-492" data-mp="3oitwodlixih9">{BUILDER_MENU_ITEM.badge}</span>
              </span>
              <span className="text-xs text-text-tertiary" data-id="element-493" data-mp="2d2vdvi0jni2u">{BUILDER_MENU_ITEM.descriptor}</span>
            </span>
            <ArrowRightIcon className="h-4 w-4 shrink-0 text-lime" aria-hidden="true" data-id="element-494" data-mp="28ynodim9hy3k" />
          </Link>
        </div>
      </div>
    </li>;
}

/** Mobile Solutions accordion with the same hierarchy as the desktop dropdown. */
export function MobileSolutionsAccordion({
  onNavigate,
  active
}: {
  onNavigate: () => void;
  active: boolean;
}) {
  const [open, setOpen] = useState(active);
  const panelId = useId();
  const linkClass = 'flex min-h-[48px] flex-col justify-center py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lime';
  return <li className="sm:col-span-2" data-id="element-495" data-mp="1wiekmbq9ql38">
      <button type="button" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen(value => !value)} className={cn('flex min-h-[48px] w-full items-center justify-between border-b border-hairline-soft text-left text-[15px] font-medium transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lime', active ? 'text-lime' : 'text-text-secondary')} data-id="element-496" data-mp="1wjpoebjprsuc">
        Solutions
        <ChevronDownIcon className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} aria-hidden="true" data-id="element-497" data-mp="p6s4eb7gej67" />
      </button>
      <div id={panelId} hidden={!open} className="border-b border-hairline-soft pb-2" data-id="element-498" data-mp="136t6ftzgfwn9">
        <Link to="/solutions" onClick={onNavigate} className={cn(linkClass, 'pl-3 text-[15px] font-semibold text-text-primary')} data-id="element-499" data-mp="y5vnvgaj90l8">
          All solutions
        </Link>
        <ul className="grid sm:grid-cols-2 sm:gap-x-6" data-id="element-500" data-mp="185crzta4z6f3">
          {SOLUTIONS_MENU.map(item => <li key={item.href} className="pl-3" data-id="element-501" data-mp="1lw9hvccoh9ig">
              <Link to={item.href} onClick={onNavigate} className={linkClass} data-id="element-502" data-mp="1lvzu7vf3vefx">
                <span className="text-[15px] font-semibold text-text-primary" data-id="element-503" data-mp="1hvz3bbatlarz">{item.label}</span>
                <span className="text-[13px] text-text-tertiary" data-id="element-504" data-mp="1vvqllo8dddbs">{item.descriptor}</span>
              </Link>
              {item.child ? <Link to={item.child.href} onClick={onNavigate} className={cn(linkClass, 'flex-row items-center justify-start gap-2 pl-4')} data-id="element-505" data-mp="1qthtsv78ghb9">
                  <CornerDownRightIcon className="h-3.5 w-3.5 shrink-0 text-lime" aria-hidden="true" data-id="element-506" data-mp="328vgp377u910" />
                  <span className="text-[14px] font-semibold text-text-primary" data-id="element-507" data-mp="378ghlj5y4rgd">{item.child.label}</span>
                </Link> : null}
            </li>)}
        </ul>
        <Link to={BUILDER_MENU_ITEM.href} onClick={onNavigate} className={cn(linkClass, 'pl-3')} data-id="element-508" data-mp="2ekkkidcgsxj2">
          <span className="flex items-center gap-2 text-[15px] font-semibold text-text-primary" data-id="element-509" data-mp="2jim5x4xd58xp">
            {BUILDER_MENU_ITEM.label}
            <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-lime" data-id="element-510" data-mp="2ognn1kb8ay6j">{BUILDER_MENU_ITEM.badge}</span>
          </span>
          <span className="text-[13px] text-text-tertiary" data-id="element-511" data-mp="1vtks125rh5l1">{BUILDER_MENU_ITEM.descriptor}</span>
        </Link>
      </div>
    </li>;
}